import { Download, Search } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'src/hooks';
import { ApiCommission, ApiRegion, ApiSpecialities } from 'src/http';
import { ApiUser } from 'src/http/api-user';
import { Region } from '../applications';
import { User } from '../applications/applications-user/BasicTable';
import { Commission } from '../commissions';
import { _Locale } from '../commissions/BasicTable';
import { Speciality } from '../specialities';
import { Params, useApi } from './useApi';

const API_URLS = [
  'https://tibtoifa.ssv.uz/api/v1/applications/get-first-table',
  'https://tibtoifa.ssv.uz/api/v1/applications/get-second-table',
  'https://tibtoifa.ssv.uz/api/v1/applications/get-third-table',
  'https://tibtoifa.ssv.uz/api/v1/applications/get-fourth-table',
  'https://tibtoifa.ssv.uz/api/v1/applications/get-fifth-table',
  'https://tibtoifa.ssv.uz/api/v1/applications/get-sixth-table',
  'https://tibtoifa.ssv.uz/api/v1/application-history/get-history',
  'https://tibtoifa.ssv.uz/api/v1/application-history',
];

const AUTH_TOKEN_KEY = 'AUTH_TOKEN_KEY';

const TablesComponent: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [activeTable, setActiveTable] = useState<number>(0);
  const token = localStorage.getItem(AUTH_TOKEN_KEY) || '';
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [params, setParams] = useState<Params>(
    activeTable !== 0
      ? { skip: (page - 1) * 10, take: 10, embed: 'user' }
      : { skip: (page - 1) * 10, take: 10, embed: 'user' }
  );

  const { data, loading, error } = useApi(API_URLS[activeTable], token, params);

  const [region, setRegion] = React.useState('0');
  const [admin, setAdmin] = React.useState('0');
  const [commission, setCommission] = React.useState('0');
  const [specialization, setSpecialization] = React.useState('0');
  const [claimedQualification, setClaimedQualification] = React.useState('0');
  const [appType, setAppType] = React.useState('0');
  const [status, setStatus] = React.useState('0');

  const handleSearch = () => {
    let temp: any = params;

    if (region !== '0') {
      temp.region = region;
    } else {
      delete params.region;
    }

    if (admin !== '0') {
      temp.user = admin;
    } else {
      delete params.user;
    }

    if (commission !== '0') {
      temp.commission = commission;
    } else {
      delete params.commission;
    }

    if (specialization !== '0') {
      temp.specialization = specialization;
    } else {
      delete params.specialization;
    }

    if (appType !== '0') {
      temp.appType = appType;
    } else {
      delete params.appType;
    }

    if (status !== '0') {
      temp.status = status;
    } else {
      delete params.status;
    }

    if (claimedQualification !== '0') {
      temp.claimedQualification = claimedQualification;
    } else {
      delete params.claimedQualification;
    }

    if (createdAtTo && createdAtFrom) {
      temp.createdAtTo = createdAtTo.toISOString().slice(0, 10);
      temp.createdAtFrom = createdAtFrom.toISOString().slice(0, 10);
    }

    setParams((prev) => ({
      ...prev,
      ...temp,
    }));
  };

  const handleTableChange = (index: number) => {
    setActiveTable(index);
    setPage(1);
    setParams((prev) => ({
      ...prev,
      skip: 0,
    }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setParams((prev) => ({
      ...prev,
      skip: value - 1,
    }));
  };

  useEffect(() => {
    if (data && data.total) {
      setTotalPages(Math.ceil(data.total / 10));
    }
  }, [data]);

  const TABLE_NAMES = [
    'Аризалар бўйича ҳисобот',
    'Янги аризалар бўйича маълумот',
    'Тасдиқланган аризалар бўйича маълумот',
    'Рад этилган аризалар бўйича маълумот',
    'Аттестация натижалари ҳисоботи',
    'Ўтказилган аттестация натижалари',
    'Админстраторлар бўйича ҳисобот',
    'Операторлар бўйича ҳисобот',
  ];

  const [regions, setRegions] = useState<Region[] | null>(null);
  const getRegions = async () => {
    let params = {
      skip: 0,
      take: 100,
    };
    try {
      const data = await ApiRegion.fetchAll(params);
      setRegions(data.hits);
    } catch ({ message }: any) {
      console.log(message);
    }
  };
  const [commissions, setCommissions] = useState<Commission[] | null>(null);
  const getCommissions = async (type?: string) => {
    let params: any = {
      skip: 0,
      take: 999,
    };
    if (appType !== '0') {
      params.appType = appType;
    } else {
      delete params.appType;
    }
    try {
      const data = await ApiCommission.fetchAll(params);
      const temp = data.hits.sort((a: any, b: any) => Number(a.publicId) - Number(b.publicId));
      setCommissions(temp);
    } catch ({ message }: any) {
      console.log(message);
    }
  };

  const [specialities, setSpecialities] = useState<Speciality[] | null>(null);
  const getSpecialities = async () => {
    let params = {
      skip: 0,
      take: 999,
    };
    try {
      const data = await ApiSpecialities.fetchAll(params);
      setSpecialities(data.hits);
    } catch ({ message }: any) {
      console.log(message);
    }
  };

  const [admins, setAdmins] = useState<User[] | null>(null);
  const getAdmins = async () => {
    let params = {
      skip: 0,
      take: 999,
    };
    try {
      const data = await ApiUser.fetchAdmins(params);
      setAdmins(data.hits);
    } catch ({ message }: any) {
      console.log(message);
    }
  };

  useEffect(() => {
    getRegions();
    getCommissions();
    getSpecialities();
  }, []);

  useEffect(() => {
    activeTable === 7 && getAdmins();
  }, [activeTable]);

  const [createdAtTo, setCreatedAtTo] = useState<Dayjs | null>(dayjs(new Date()));
  const [createdAtFrom, setCreatedAtFrom] = useState<Dayjs | null>(
    dayjs(new Date().setFullYear(2022))
  );

  const onDownload_STATUS_RESULTS = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const url = `https://tibtoifa.ssv.uz/api/v1/application-history/get-history-to-excel`;
    // let url = `https://tibtoifa.ssv.uz/api/v1/applications/download-app-with-status?appType=${activeAppType}&status=${activeStatus}`;
    // url =
    //   activeCommission && activeCommission !== '0' ? url + `&commission=${activeCommission}` : url;
    // url = activeRegion && activeRegion !== '0' ? url + `&region=${activeRegion}` : url;
    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.blob())
      .then((myBlob) => {
        const url = window.URL.createObjectURL(myBlob);
        const link = document.createElement('a');
        link.href = url;
        let today: Date | string = new Date();
        let dd = String(today.getDate()).padStart(2, '0');
        let mm = String(today.getMonth() + 1).padStart(2, '0');
        let yyyy = today.getFullYear();
        today = mm + '_' + dd + '_' + yyyy;
        link.setAttribute('download', 'Админстраторлар бўйича ҳисобот_' + today + '.xlsx'); //or any other extension
        document.body.appendChild(link);
        link.click();
      });
  };

  const renderApplicationsTable = () => {
    const applications = data.applications;
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('all')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('appealStatus.PENDING')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('applicationStatus.confirmed')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('applicationStatus.declined')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('appealStatus.TEST_TAKERS')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('appealStatus.VERBAL_PASSERS')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('appealStatus.THOSE_WHO_FAILED_TEST')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('appealStatus.THOSE_WHO_DID_NOT_COME_TEST_TWICE')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('appealStatus.THOSE_WHO_DID_NOT_COME_VERBAL_TWICE')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{applications?.all}</TableCell>
              <TableCell>{applications?.allPending}</TableCell>
              <TableCell>{applications?.allConfirmed}</TableCell>
              <TableCell>{applications?.allDeclined}</TableCell>
              <TableCell>{applications?.allTestTakers}</TableCell>
              <TableCell>{applications?.allVerbalPassers}</TableCell>
              <TableCell>{applications?.allThoseWhoFailedTest}</TableCell>
              <TableCell>{applications?.allThoseWhoDidNotComeTestTwice}</TableCell>
              <TableCell>{applications?.allThoseWhoDidNotComeVerbalTwice}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTableHeaders = () => (
    <Grid container spacing={2}>
      <Grid item xs={2}>
        <Typography variant="h3" pl={3} py={2}>
          ID
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="h3" pl={3} py={2}>
          Manba
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="h3" pl={3} py={2}>
          {t('name.full')}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="h3" pl={3} py={2}>
          Turi
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="h3" pl={3} py={2}>
          {t('date')}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderTableRows = (hit: any) => (
    <Grid container spacing={2}>
      <Grid item xs={2}>
        {hit.id}
      </Grid>
      <Grid item xs={2}>
        {hit.appFrom === 'mygov.uz' ? (
          <img src="/assets/my.gov.uz.svg" alt="" width="150" />
        ) : (
          hit.appFrom
        )}
      </Grid>
      <Grid item xs={4}>
        {`${hit.user?.firstName || ''} ${hit.user?.lastName || ''} ${hit.user?.middleName || ''}`}
      </Grid>
      <Grid item xs={2}>
        {t(
          [
            'Врачлар ва фармацевтлар',
            '“Олий ҳамширалик иши” мутахассислигига эга бўлган ҳамширалар, ўрта тиббиёт ва ўрта фармацевтика ходимлари',
            'Бирламчи тиббиёт ходимлари',
          ][Number(hit.appType) - 1]
        )}
      </Grid>
      <Grid item xs={2}>
        {new Date(hit.createdAt).toLocaleDateString()}
      </Grid>
    </Grid>
  );

  const renderDetails = (hit: any) => (
    <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
      <TableContainer component={Paper} sx={{ flex: 1 }}>
        <Table size="small">
          <TableBody>
            {[
              { label: t('Ariza raqami'), value: hit.uid },
              { label: t('My.gov.uz raqami'), value: hit.taskId },
              { label: t('workplace'), value: hit.workplace },
              { label: t('position'), value: hit.position },
              { label: t('specialty', 'Mutaxassislik'), value: hit.specialty },
              {
                label: t('attestationResult'),
                value: hit.attestationResult && (
                  <Typography
                    bgcolor={'success.main'}
                    display={'inline-flex'}
                    px={1}
                    py={0.5}
                    borderRadius={1}
                    color={'#fff'}
                  >
                    {hit.attestationResult}
                  </Typography>
                ),
              },
              { label: t('status'), value: t('appealStatus.' + hit.status) },
              { label: t('reason'), value: hit.reason },
              { label: t('profile.phone'), value: hit.indPhone },
              { label: 'my.gov.uz holati', value: hit.epiguStatus },
              { label: t('presentation'), value: hit.presentation },
              { label: t('Steps'), value: hit.steps },
              { label: 'Sertifikat seriya va raqami', value: hit.certSeriesNumber },
              { label: t('nameMedicalEducational'), value: hit.nameMedicalEducational },
              {
                label: t('copyDiploma'),
                value: (
                  <a href={hit.copyDiploma} target="_blank" rel="noopener noreferrer">
                    <RemoveRedEyeIcon />
                  </a>
                ),
              },
              {
                label: t('copyDocTraining', 'Xorijiy davlatlarda olingan hujjatlarning nusxasi'),
                value: (
                  <a href={hit.copyDocTraining} target="_blank" rel="noopener noreferrer">
                    <RemoveRedEyeIcon />
                  </a>
                ),
              },
              { label: t('qualificationCategory'), value: hit.qualificationCategory },
              { label: t('qualificationCategoryIssued'), value: hit.qualificationCategoryIssued },
              {
                label: t('qualificationCategorySpecialty'),
                value: hit.qualificationCategorySpecialty,
              },
              {
                label: t('copyQualificationCategory'),
                value: (
                  <a href={hit.copyQualificationCategory} target="_blank" rel="noopener noreferrer">
                    <RemoveRedEyeIcon />
                  </a>
                ),
              },
              {
                label: t('claimedQualification'),
                value: t(
                  ['Олий', 'Биринчи', 'Иккинчи', 'Учинчи'][Number(hit.claimedQualification) - 1]
                ),
              },
              { label: t('creditAccumulated'), value: hit.creditAccumulated },
              {
                label: t('copyCertificateQualification'),
                value: (
                  <a
                    href={hit.copyCertificateQualification}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <RemoveRedEyeIcon />
                  </a>
                ),
              },
              {
                label: t('jobs.historyBook'),
                value: (
                  <a href={hit.documentWork} target="_blank" rel="noopener noreferrer">
                    <RemoveRedEyeIcon />
                  </a>
                ),
              },
              { label: t('workExperienceYear'), value: hit.workExperienceYear },
              { label: t('specialtyWorkExperience'), value: hit.specialtyWorkExperience },

              {
                label: t('region'),
                value: regions?.filter((c) => c.id === hit.regionId)[0]?.title[
                  i18n.language as _Locale
                ],
              },

              { label: t('hisSpecialty'), value: hit.hisSpecialty },
              { label: t('electronicWorkBook'), value: hit.electronicWorkBook },
              {
                label: t('Invoice'),
                value: (
                  <a href={hit.invoice} target="_blank" rel="noopener noreferrer">
                    <RemoveRedEyeIcon />
                  </a>
                ),
              },
              { label: 'Operator', value: hit.fromOperator },
              {
                label: t('Муддатидан аввал тоифа олувчи'),
                value: hit.prematureRecipient ? t('yes') : t('no'),
              },
              { label: t('applicationStatus.confirmed'), value: hit.confirmedBy },
              { label: t('appealStatus.DECLINED'), value: hit.declinedBy },
              // { label: 'Region', value: hit.region },
              {
                label: t('commission'),
                value: commissions?.filter((c) => c.id === hit.commissionId)[0]?.title[
                  i18n.language as _Locale
                ],
              },
              {
                label: t('speciality'),
                value: specialities?.filter((c) => c.id === hit.specializationId)[0]?.title[
                  i18n.language as _Locale
                ],
              },
              // { label: 'District', value: hit.district },
              // { label: 'Direction', value: hit.direction },
              { label: t('attestations.address'), value: hit.attestationLocation },
              { label: 'Yaratilgan', value: new Date(hit.createdAt).toLocaleDateString() },
              { label: 'Yangilangan', value: new Date(hit.updatedAt).toLocaleDateString() },
            ].map((row, index) => (
              <TableRow key={index} sx={{ bgcolor: index % 2 ? 'grey.100' : 'white' }}>
                <TableCell>{row.label}</TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer component={Paper} sx={{ flex: 1 }}>
        <Table size="small">
          <TableBody>
            {hit.user?.passportPhoto && (
              <TableRow>
                <TableCell>Passport Photo</TableCell>
                <TableCell>
                  <img
                    src={`data:image/jpeg;base64,${hit.user.passportPhoto}`}
                    alt="Passport"
                    style={{ width: '100px', height: '100px' }}
                  />
                </TableCell>
              </TableRow>
            )}
            {[
              { label: t('profile.firstName'), value: hit.user?.firstName },
              { label: t('profile.lastName'), value: hit.user?.lastName },
              { label: t('profile.middleName'), value: hit.user?.middleName },
              { label: t('profile.pin'), value: hit.user?.pin },
              { label: t('profile.tin'), value: hit.user?.tin },
              { label: t('profile.passportSeria'), value: hit.user?.passportSeries },
              { label: t('profile.passportNumber'), value: hit.user?.passportNumber },
              { label: t('profile.passportIssueDate'), value: hit.user?.passportIssueDate },
              { label: t('profile.passportIssuePlace'), value: hit.user?.passportIssuePlace },
              { label: t('profile.passportExpireDate'), value: hit.user?.passportExpireDate },
              { label: t('profile.dob'), value: hit.user?.dob },
              { label: t('Fuqaroligi'), value: hit.user?.citizenship },
              { label: 'Millati', value: hit.user?.nationality },
              { label: "Tug'ilgan manzili", value: hit.user?.birthPlace },
              {
                label: t('profile.gender'),
                value:
                  hit.user?.gender === 1
                    ? t('gender.MALE')
                    : hit.user?.gender === 2
                    ? t('gender.FEMALE')
                    : '',
              },
              { label: 'Doimiy manzil', value: hit.user?.perAddress },
              { label: 'Phone', value: hit.user?.phone },
              { label: 'Email', value: hit.user?.email },
              {
                label: 'Region ID',
                value: regions?.filter((c) => c.id === hit.user?.regionId)[0]?.title[
                  i18n.language as _Locale
                ],
              },
            ].map((row, index) => (
              <TableRow key={index} sx={{ bgcolor: index % 2 ? 'grey.100' : 'white' }}>
                <TableCell>{row.label}</TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderTable = () => {
    if (loading) return <CircularProgress />;
    if (error) return <Typography color="error">{error}</Typography>;

    if (activeTable === 6) {
      return renderLastTable();
    }

    if (activeTable === 0) {
      return renderApplicationsTable();
    }

    if (activeTable === 7) {
      return renderTableAfterLastTable();
    }

    const hits = data.hits;

    return (
      <Box>
        {renderTableHeaders()}
        {hits?.map((hit: any) => (
          <Accordion key={hit.id} sx={{ width: '100%' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ width: '100%' }}>
              {renderTableRows(hit)}
            </AccordionSummary>
            <AccordionDetails sx={{ width: '100%' }}>{renderDetails(hit)}</AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  const renderLastTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('role')}</TableCell>
            <TableCell>{t('name.full')}</TableCell>
            <TableCell>{t('appealStatus.CONFIRMED')}</TableCell>
            <TableCell>{t('appealStatus.DECLINED')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.hits?.map((row: any, index: number) => (
            <TableRow key={index}>
              <TableCell>{t('role.' + row.role)}</TableCell>
              <TableCell>{row.userName + ' ' + row.lastName}</TableCell>
              <TableCell>{row.confirmedCount}</TableCell>
              <TableCell>{row.declinedCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTableAfterLastTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>{t('date')}</TableCell>
            <TableCell>{t('reason')}</TableCell>
            <TableCell>{t('status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.hits?.map((row: any, index: number) => (
            <TableRow key={index}>
              <TableCell>
                <Box sx={{ fontWeight: 'bold' }}>{row.id}</Box>
              </TableCell>
              <TableCell>{dateFormat(row.createdAt)}</TableCell>
              <TableCell>{row.message}</TableCell>
              <TableCell>{t('appealStatus.' + row.status)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box display="flex" gap={2} mb={3}>
        {API_URLS.map((url, index) => (
          <Button
            key={index}
            variant={activeTable === index ? 'contained' : 'outlined'}
            onClick={() => handleTableChange(index)}
          >
            {`Jadval № ${index + 1}`}
          </Button>
        ))}
      </Box>
      <br />
      <Typography variant="h2" my={2}>
        {TABLE_NAMES[activeTable]}
      </Typography>
      <br />
      <Grid container spacing={2}>
        {activeTable !== 6 && activeTable !== 7 && (
          <>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="appType">{t('attestations.type')}</InputLabel>
                <Select
                  labelId="appType"
                  value={appType}
                  onChange={(e) => setAppType(e.target.value)}
                >
                  <MenuItem value="0">{t('action.select')}</MenuItem>
                  {[
                    'Врачлар ва фармацевтлар',
                    '“Олий ҳамширалик иши” мутахассислигига эга бўлган ҳамширалар, ўрта тиббиёт ва ўрта фармацевтика ходимлари',
                    'Бирламчи тиббиёт ходимлари',
                  ]?.map((c: string, index: number) => (
                    <MenuItem value={index + 1} key={index}>
                      {t(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="status">{t('status')}</InputLabel>
                <Select labelId="status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="0">{t('action.select')}</MenuItem>
                  <MenuItem value={'PENDING'}> {t('appealStatus.PENDING')}</MenuItem>
                  <MenuItem value={'PASSED'}> {t('appealStatus.PASSED')}</MenuItem>
                  <MenuItem value={'FAILED'}> {t('appealStatus.FAILED')}</MenuItem>
                  <MenuItem value={'APPEAL_CONFIRMED'}>
                    {' '}
                    {t('appealStatus.APPEAL_CONFIRMED')}
                  </MenuItem>
                  <MenuItem value={'APPEAL_PENDING'}> {t('appealStatus.APPEAL_PENDING')}</MenuItem>
                  <MenuItem value={'APPEAL_DECLINED'}>
                    {' '}
                    {t('appealStatus.APPEAL_DECLINED')}
                  </MenuItem>
                  <MenuItem value={'DECLINED'}> {t('appealStatus.DECLINED')}</MenuItem>
                  <MenuItem value={'CONFIRMED'}> {t('appealStatus.CONFIRMED')}</MenuItem>
                  <MenuItem value={'REJECTED_FOR_REVIEW'}>
                    {' '}
                    {t('appealStatus.REJECTED_FOR_REVIEW')}
                  </MenuItem>
                  <MenuItem value={'APPLICATION_FOR_RECONSIDERATION'}>
                    {' '}
                    {t('appealStatus.APPLICATION_FOR_RECONSIDERATION')}
                  </MenuItem>
                  <MenuItem value={'REJECTED_DUE_TO_NON_IMPLEMENTATION'}>
                    {' '}
                    {t('appealStatus.REJECTED_DUE_TO_NON_IMPLEMENTATION')}
                  </MenuItem>
                  <MenuItem value={'PERMISSION_TO_PAYMENT'}>
                    {' '}
                    {t('appealStatus.PERMISSION_TO_PAYMENT')}
                  </MenuItem>
                  <MenuItem value={'PAYMENT_DONE'}> {t('appealStatus.PAYMENT_DONE')}</MenuItem>
                  <MenuItem value={'VERBAL_PASSERS'}> {t('appealStatus.VERBAL_PASSERS')}</MenuItem>
                  <MenuItem value={'TEST_TAKERS'}> {t('appealStatus.TEST_TAKERS')}</MenuItem>
                  <MenuItem value={'THOSE_WHO_FAILED_TEST'}>
                    {' '}
                    {t('appealStatus.THOSE_WHO_FAILED_TEST')}
                  </MenuItem>
                  <MenuItem value={'PAYMENT_DONE'}> {t('appealStatus.PAYMENT_DONE')}</MenuItem>
                  <MenuItem value={'THOSE_WHO_DID_NOT_COME_TEST_TWICE'}>
                    {' '}
                    {t('appealStatus.THOSE_WHO_DID_NOT_COME_TEST_TWICE')}
                  </MenuItem>
                  <MenuItem value={'THOSE_WHO_DID_NOT_COME_VERBAL_TWICE'}>
                    {' '}
                    {t('appealStatus.THOSE_WHO_DID_NOT_COME_VERBAL_TWICE')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="region-label">{t('region')}</InputLabel>
                <Select
                  labelId="region-label"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                >
                  <MenuItem value="0">{t('action.select')}</MenuItem>
                  {regions?.map((c: Region, index: number) => (
                    <MenuItem value={c.id} key={index}>
                      {c.title[i18n.language as _Locale]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="speciality-label">{t('speciality')}</InputLabel>
                <Select
                  labelId="speciality-label"
                  value={specialization}
                  onChange={(e) => setSpecialization(e.target.value)}
                >
                  <MenuItem value="0">{t('action.select')}</MenuItem>
                  {specialities?.map((spec) => (
                    <MenuItem key={spec.id} value={spec.id}>
                      {spec.title[i18n.language as _Locale]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="commission-label">{t('commission')}</InputLabel>
                <Select
                  labelId="commission-label"
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                >
                  <MenuItem value="0">{t('action.select')}</MenuItem>
                  {commissions?.map((c: Commission, index: number) => (
                    <MenuItem value={c.id} key={index}>
                      {c.title[i18n.language as _Locale]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="claimedQualification">{t('claimedQualification')}</InputLabel>
                <Select
                  labelId="claimedQualification"
                  value={claimedQualification}
                  onChange={(e) => setClaimedQualification(e.target.value)}
                >
                  <MenuItem value="0">{t('action.select')}</MenuItem>
                  {['Олий', 'Биринчи', 'Иккинчи', 'Учинчи']?.map((c: string, index: number) => (
                    <MenuItem value={index + 1} key={index}>
                      {t(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        {activeTable === 7 && (
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="region-label">{t('Operator')}</InputLabel>
              <Select
                labelId="region-label"
                value={admin}
                onChange={(e) => setAdmin(e.target.value)}
              >
                <MenuItem value="0">{t('action.select')}</MenuItem>
                {admins?.map((c: User, index: number) => (
                  <MenuItem value={c.id} key={index}>
                    {c.firstName + ' ' + c.lastName + ' ' + c.middleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer sx={{ p: 0 }} components={['DatePicker', 'DatePicker']}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Sanadan"
                value={createdAtFrom}
                onChange={(newValue) => setCreatedAtFrom(newValue)}
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer sx={{ p: 0 }} components={['DatePicker', 'DatePicker']}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Sanagacha"
                value={createdAtTo}
                onChange={(newValue) => setCreatedAtTo(newValue)}
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            variant="contained"
            sx={{ mt: 3, minWidth: 'unset', borderRadius: 1, py: 1.5 }}
            onClick={() => handleSearch()}
            size="large"
            fullWidth
          >
            <Search sx={{ mr: 1 }} />
            {t('search')}
          </Button>
        </Grid>
      </Grid>
      <br />
      {activeTable === 6 && (
        <Box display={'flex'} alignItems={'center'} mb={3}>
          <Button
            onClick={onDownload_STATUS_RESULTS}
            variant="contained"
            color="info"
            sx={{ p: '5px 10px', ml: 4 }}
          >
            <Download /> &nbsp;
            {t('Админстраторлар бўйича ҳисобот')}
          </Button>
        </Box>
      )}
      {renderTable()}
      {activeTable !== 0 && (
        <Box display="flex" justifyContent="center" mt={10}>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
        </Box>
      )}
    </Box>
  );
};

export default TablesComponent;
